import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  MenuItem,
  InputAdornment,
  OutlinedInput,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Box,
  Grid,
  Typography,
  Tooltip,
  Slider,
  IconButton,
  Button,
  ToggleButtonGroup, ToggleButton, CardHeader
} from '@mui/material';
import CreateCollectionButton from "../../pages/custom-components/CreateCollectionButton";
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import { usePageSettings } from '../../contexts/PageSettingsContext';
import { useSelectedCard } from '../../contexts/SelectedCardContext';
import {useNavigationHistory} from "@/contexts/NavigationContext";
import {useClipActiveFilters} from "../../contexts/application/ClipActiveFilters";
import {useKeyMomentActiveFilters} from "../../contexts/application/KeyMomentActiveFilters";
import {BACKUP_PAGE_NAME, EVENTS_PAGE_NAME, EVENTS_PAGE_TITLE} from "@/constants/globalConstants";
import { circularIconHoverSx, HEADER_ACTION_ICON_SIZE } from "@/styles/iconHover";

// third-party
import { FormattedMessage } from 'react-intl';

// project imports
import MainCard from 'components/MainCard';
import navigation from 'menu-items';
import { ThemeDirection } from 'config';

// assets
import ApartmentOutlined from '@ant-design/icons/ApartmentOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import SortAscendingOutlined from '@ant-design/icons/SortAscendingOutlined';
import SortDescendingOutlined from '@ant-design/icons/SortDescendingOutlined';
import HomeFilled from '@ant-design/icons/HomeFilled';
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import PushPinIcon from "@mui/icons-material/PushPin";
import { SlashedIcon } from '../../utils/utils';

import {
  KEY_MOMENTS_SORTABLE_FIELDS,
  CURRENT_SPORT, KEY_MOMENT_PAGE_NAME, CLIP_PAGE_NAME, LIBRARY_PAGE_NAME,
} from '../../constants/globalConstants';
import { stripValidRoot } from '../../utils/utils'
import {DimensionCardsMulti, useDimensionCardProvider} from "@/contexts/application/DimensionCardsProvider";
import TableChartIcon from "@mui/icons-material/TableChart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonalVideoIcon from '@mui/icons-material/PersonalVideo';
import DeleteIcon from "@mui/icons-material/Delete";
import {deleteKeyMomentCutMaster} from "@/queries/keyMomentQueries";
import {deleteLogicClip} from "@/queries/clipQueries";
import {enqueueSnackbar} from "notistack";
import StyledPopper from "@/pages/custom-components/StyledPopper";
import {VolumeControl} from "@/pages/custom-components/CommonComponents/VolumeControl";
import {useSSEProvider} from "@/contexts/application/SSEProvider";

export default function Breadcrumbs({
  card = false,
  custom = false,
  divider = false,
  heading,
  icon,
  icons,
  links,
  maxItems,
  rightAlign,
  separator,
  title = true,
  titleBottom = true,
  sx,
  ...others
}) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const {setClipActiveFilters} = useClipActiveFilters();
  const {setKeyMomentActiveFilters} = useKeyMomentActiveFilters();
  const { handleToggle, volume, setVolume, muted, setMuted, ingestionViewMode, setIngestionViewMode, carouselOverflows, carouselPinned, setCarouselPinned, hasLiveJobs } = usePageSettings();
  const { filtersDrawerOpen, setFiltersDrawerOpen, selectedClipCards, clearClipCards, selectedKeyMomentCards, clearKeyMomentCards, selectedCard, resetSelectionContext } = useSelectedCard();
  const { previousPage: historyPreviousPage, currentPage } = useNavigationHistory();
  const [main, setMain] = useState();
  const [item, setItem] = useState();
  const [pinHovered, setPinHovered] = useState(false);
  const [sortField, setSortField] = useState();
  const [openPopper, setOpenPopper] = useState(false)
  const [order, setOrder] = useState("desc");
  const isFirstRender = React.useRef(true);
  const {dimensionCard, setDimensionCard} = useDimensionCardProvider()

  const path = location.pathname;
  const isKeyMoments = path.includes(KEY_MOMENT_PAGE_NAME);
  const isClips = path.includes(CLIP_PAGE_NAME);
  const isNewCollection = path.includes('newcollection');
  const isEditCollection = path.includes('editcollection');
  const isLibrary = path.includes(LIBRARY_PAGE_NAME);

  const hasNonCustomKeyMoments = selectedKeyMomentCards.some(el => !el.is_custom);
  const hasNonCustomCards = selectedCard.some(el => !el.is_custom);;
  const noKeyMomentsSelected = selectedKeyMomentCards.length === 0;
  const noClipsSelected = selectedClipCards.length === 0;
  const noCardSelected = selectedCard.length === 0;

  const [runningIngestionJobs, setRunningIngestionJobs] = useState([]);
  const { jobs } = useSSEProvider();

  useEffect(() => {
    if (!jobs || jobs.length === 0) {
      setRunningIngestionJobs([]);
      return;
    }

    const ingestionJobsByEvent = {};

    const getSafeTimestamp = (dateValue) => {
      if (!dateValue) return 0;
      const safeString = typeof dateValue === 'string' ? dateValue.replace(' ', 'T') : dateValue;
      const time = new Date(safeString).getTime();
      return isNaN(time) ? 0 : time;
    };

    // Raggruppa i job per evento, mantenendo solo il più recente
    jobs.forEach(job => {
      if (typeof job.type === 'string' && job.type.toLowerCase().includes('ingestion') && job.events?.length > 0) {
        const eventId = String(job.events[0].id);
        const existing = ingestionJobsByEvent[eventId];
        const currentJobTime = getSafeTimestamp(job.last_status_change || job.updated_at || job.created_at);

        if (!existing) {
          ingestionJobsByEvent[eventId] = job;
        } else {
          const existingTime = getSafeTimestamp(existing.last_status_change || existing.updated_at || existing.created_at);
          if (currentJobTime > existingTime) {
            ingestionJobsByEvent[eventId] = job;
          }
        }
      }
    });

    // Filtra solo i job attivi e rimappali nel formato atteso dal Carosello
    const activeJobs = Object.values(ingestionJobsByEvent)
        .filter(job => job.status.toLowerCase() === 'running' || job.status.toLowerCase() === 'starting')
        .map(job => ({ job, event: job.events[0] }));

    setRunningIngestionJobs(activeJobs);
  }, [jobs]);

  const isVisibleDimensionCard = () => {
    return ( (window.location.pathname.includes(EVENTS_PAGE_NAME) && !!hasLiveJobs) ||
        ((window.location.pathname.includes('keymoments') ||
            window.location.pathname.includes('clips')) && !window.location.pathname.includes('edit')))
  }

  const shouldShowDeleteButton = (isKeyMoments || isClips) && !isNewCollection && !isEditCollection;

  const isDeleteDisabled =
      !(isClips || isKeyMoments || isLibrary) ||
      (isLibrary && (noCardSelected || hasNonCustomCards)) ||
      (isKeyMoments && !isLibrary && (hasNonCustomKeyMoments || noKeyMomentsSelected)) ||
      (isClips && noClipsSelected);

  const getTooltipTitle = () => {
    if (isLibrary) {
      if (noCardSelected) return "No key moments selected";
      if (hasNonCustomCards) return "Can't delete non-custom key moments";
      return "Delete selected key moments";
    }

    if (isKeyMoments) {
      if (noKeyMomentsSelected) return "No key moments selected";
      if (hasNonCustomKeyMoments) return "Can't delete non-custom key moments";
      return "Delete selected key moments";
    }

    if (isClips) {
      if (noClipsSelected) return "No clips selected";
      return "Delete selected clips";
    }

    return "Delete";
  };

  const anchorRef = useRef(null)

  const iconSX = {
    marginRight: theme.direction === ThemeDirection.RTL ? 0 : theme.spacing(0.75),
    marginLeft: theme.direction === ThemeDirection.RTL ? theme.spacing(0.75) : 0,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };

  useEffect(() => {
    if (isFirstRender.current) {
      // salta la prima esecuzione
      isFirstRender.current = false;
      return;
    }
    const newOrdering = {sort_by: sortField, sort_order: order}
    if(location.pathname.includes('/clips'))
      setClipActiveFilters(prev => ({
        ...prev,
        ...newOrdering
      }));
    else
      setKeyMomentActiveFilters(prev => ({
        ...prev,
        ...newOrdering
      }));
  }, [order, sortField]);

  const toggleOrder = () => {
    setOrder(prev => prev === "asc" ? "desc" : "asc");
  };

  let customLocation = stripValidRoot(location.pathname)

  useEffect(() => {
    setSortField(KEY_MOMENTS_SORTABLE_FIELDS[CURRENT_SPORT].platform[0].id)
  }, [])

  useEffect(() => {
    if(window.location.pathname.includes('/keymoments/library') && (dimensionCard === DimensionCardsMulti.MEDIUM))
        setDimensionCard(DimensionCardsMulti.SMALL)
  }, [window.location.pathname]);

  useEffect(() => {
    if(location.pathname.includes('library')) setSortField(KEY_MOMENTS_SORTABLE_FIELDS[CURRENT_SPORT].platform[0].id)
  }, [location])
  
  useEffect(() => {
    navigation?.items?.map((menu) => {
      if (menu.type && menu.type === 'group') {
        if (menu?.url && menu.url === customLocation) {
          setMain(menu);
          setItem(menu);
        } else {
          getCollapse(menu);
        }
      }
      return false;
    });
  });

  const handleDimensionChange = (event) => {
    setDimensionCard(event.target.value);
  };

  // set active item state
  const getCollapse = (menu) => {
    if (!custom && menu.children) {
      menu.children.filter((collapse) => {
        if (collapse.type && collapse.type === 'collapse') {
          getCollapse(collapse);
          if (collapse.url === customLocation) {
            setMain(collapse);
            setItem(collapse);
          }
        } else if (collapse.type && collapse.type === 'item') {
          if (customLocation === collapse.url || customLocation?.startsWith(collapse.url + '/')) {
            setMain(menu);
            setItem(collapse);
          }
        }
        return false;
      });
    }
  };

  const deleteSelectedCards = async (option) => {
    if (option === 'NO') return setOpenPopper(false);

    const isKeyMoments = location.pathname.includes('keymoments');
    const isClips = location.pathname.includes('clips');


    let deletePromises = [];
    if (isLibrary){
      deletePromises = selectedCard.map(el => deleteKeyMomentCutMaster(el.id));
    }else if (isKeyMoments) {
      deletePromises = selectedKeyMomentCards.map(el => deleteKeyMomentCutMaster(el.id));
    } else if (isClips) {
      deletePromises = selectedClipCards.map(el => deleteLogicClip(el.id));
    }

    if (deletePromises.length > 0) {
      try {
        await Promise.all(deletePromises);
        enqueueSnackbar('Selected cards successfully deleted');
      } catch (error) {
        console.error(error);
      }
    }

    if (isLibrary){
      resetSelectionContext()
    }else if (isKeyMoments) {
      clearKeyMomentCards()
    } else if (isClips) {
      clearClipCards()
    }

    setOpenPopper(false);
  };

  // item separator
  const SeparatorIcon = separator;
  const separatorIcon = separator ? <SeparatorIcon style={{ fontSize: '0.75rem', marginTop: 2 }} /> : '/';

  let mainContent;
  let itemContent;
  let breadcrumbContent = <Typography />;
  let itemTitle = '';
  let CollapseIcon;
  let ItemIcon;
  let tempContent;

  const subRoutes = {
    'newevent': 'New Event',
    'processing': 'Processing',
    'library': 'Library',
    'newcollection': 'New Collection',
    'editcollection': 'Save Collection',
  }

  const mapMainRoutes = {
    [EVENTS_PAGE_TITLE]: EVENTS_PAGE_NAME,
    'Key moments': 'keymoments',
    'Clips': 'clips',
    'Rendering Jobs': 'renderingjob',
    'Automations': 'rules',
    'Dashboard': 'dashboard',
  }

  const handleToggleMute = () => {
    setMuted(!muted);
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
    // if (newValue === 0) setMuted(true);
    // else setMuted(false);
  };

  function extractIdsFromChildren(node) {
    if (!node) return [""]; // undefined o null → stringa vuota
    if (Array.isArray(node)) {
      return node.flatMap(extractIdsFromChildren); // ricorsione sugli array
    }
    if (React.isValidElement(node)) {
      const currentId = String(node.props?.id).toLowerCase() ?? "";      
      const childIds = extractIdsFromChildren(node.props.children);
      return [ ...childIds, currentId ];
    }
    return [];
  }


  // collapse item
  if (main && main.type === 'collapse' && !main.breadcrumbs) {
    CollapseIcon = main.icon ? main.icon : ApartmentOutlined;
    mainContent = (
      <Typography
        {...(main.url && { component: Link, to: main.url })}
        variant={window.location.pathname === main.url ? 'subtitle1' : 'h6'}
        sx={{ textDecoration: 'none' }}
        color={window.location.pathname === main.url ? 'text.primary' : 'text.secondary'}
      >
        {icons && <CollapseIcon style={iconSX} />}
        <FormattedMessage id={main?.title} />
      </Typography>
    );

    if (!!custom) {
      breadcrumbContent = (
        <MainCard
          border={card}
          sx={card === false ? { mb: 3, bgcolor: 'inherit', backgroundImage: 'none', ...sx } : { mb: 3, ...sx }}
          {...others}
          content={card}
          shadow="none"
        >
          <Grid
            container
            direction={rightAlign ? 'row' : 'column'}
            justifyContent={rightAlign ? 'space-between' : 'flex-start'}
            alignItems={rightAlign ? 'center' : 'flex-start'}
            spacing={1}
          >
            <Grid>
              <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
                <Typography component={Link} to="/" color="text.secondary" variant="h6" sx={{ textDecoration: 'none' }}>
                  {icons && <HomeOutlined style={iconSX} />}
                  {icon && !icons && <HomeFilled style={{ ...iconSX, marginRight: 0 }} />}
                  {(!icon || icons) && <FormattedMessage id="home" />}
                </Typography>
                {mainContent}
              </MuiBreadcrumbs>
            </Grid>
            {title && titleBottom && (
              <Grid sx={{ mt: card === false ? 0.25 : 1 }}>
                <Typography variant="h2">
                  <FormattedMessage id={main.title} />
                </Typography>
              </Grid>
            )}
          </Grid>
          {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
        </MainCard>
      );
    }
  }

  // items
  if ((item && item.type === 'item') || (item?.type === 'group' && item?.url) || custom) {    
    itemTitle = item?.title;

    ItemIcon = item?.icon ? item.icon : ApartmentOutlined;
    itemContent = (
      <Typography variant="subtitle1" color="text.primary" sx={{cursor:'pointer'}} onClick={() => {
          if(segments.length > 2 && !itemList.includes(lastSegment)) navigate(mapMainRoutes[itemTitle])}}>
        {icons && <ItemIcon style={iconSX} />}
        {itemTitle}
      </Typography>
    );

    const segments = location.pathname.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1];

    const itemList = extractIdsFromChildren(itemContent);
    let extraBreadcrumb = null;

    if (segments.length > 2 && !itemList.includes(lastSegment)) {
      extraBreadcrumb = (
        <Typography color="text.primary" variant="h6">
          {subRoutes[lastSegment]}
        </Typography>
      );
    }

    tempContent = (
      <MuiBreadcrumbs
        sx={{
          height: '45px',
          display: 'flex',
          alignItems: 'center' // ✅ centra verticalmente il contenuto
        }}
        aria-label="breadcrumb"
        maxItems={maxItems || 8}
        separator={separatorIcon}
      >
        <Typography to="/" color="text.secondary" variant="h6" sx={{ textDecoration: 'none' }}>
          {icons && <HomeOutlined style={iconSX} />}
          {icon && !icons && <HomeFilled style={{ ...iconSX, marginRight: 0 }} />}
          {(!icon || icons) && <FormattedMessage id="home" />}
        </Typography>
        {mainContent}
        {itemContent}
        {extraBreadcrumb && extraBreadcrumb}
      </MuiBreadcrumbs>
    );

    if (custom && links && links?.length > 0) {
      tempContent = (
        <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
          {links?.map((link, index) => {
            CollapseIcon = link.icon ? link.icon : ApartmentOutlined;

            return (
              <Typography
                key={index}
                {...(link.to && { component: Link, to: link.to })}
                variant={!link.to ? 'subtitle1' : 'h6'}
                sx={{ textDecoration: 'none' }}
                color={!link.to ? 'text.primary' : 'text.secondary'}
              >
                {link.icon && <CollapseIcon style={iconSX} />}
                <FormattedMessage id={link.title} />
              </Typography>
            );
          })}
        </MuiBreadcrumbs>
      );
    }
  }

  return (
    <MainCard
      border={card}
      sx={card === false ? { bgcolor: 'inherit', backgroundImage: 'none', ...sx } : { ...sx }}
      {...others}
      content={card}
      shadow="none"
    >
      <Grid
        container
        direction={rightAlign ? 'row' : 'column'}
        justifyContent={rightAlign ? 'space-between' : 'flex-start'}
        alignItems={rightAlign ? 'center' : 'flex-start'}
        spacing={1}
      >
        {title && !titleBottom && (
          <Grid>
            <Typography variant="h2">
              <FormattedMessage id={custom ? heading : item?.title} />
            </Typography>
          </Grid>
        )}

        {!['editor'].includes(location.pathname.split('/')[location.pathname.split('/').length-1]) && <Box
          sx={{
            width: '100%',
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Grid>{tempContent}</Grid>

          {/* Gruppo Toggle + Filtro */}
          <Box sx={{ display: "flex", height:'45px', alignItems: "center", gap: 2 }} data-tour={"breadcrumbs_total_toggles_right"}>
            {/* Pulsante create collection */}
            {['keymoments','clips'].includes(location.pathname.split('/')[location.pathname.split('/').length-1]) && <CreateCollectionButton clip_type={itemTitle} flagIcon={true} redirectHome={true} flagDisabled={false}/>}

            {/*Size delle cards indipendente dalla dimensione dello schermo*/}
            {isVisibleDimensionCard() && (
                <FormControl size="small" data-tour={"dimension_changer_breadcrumbs"}>
                  <Select
                      value={dimensionCard}
                      onChange={handleDimensionChange}
                      aria-label="Card Dimension"
                      sx={{
                        height: 32,
                        minWidth: 130,
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          paddingTop: 0,
                          paddingBottom: 0,
                        }
                      }}
                  >
                    <MenuItem value={DimensionCardsMulti.SMALL}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box width={24} display={'flex'} justifyContent={'center'}>
                          <PersonalVideoIcon sx={{ fontSize: '12px' }} />
                        </Box>
                        <Typography variant="body2">Small</Typography>
                      </Box>
                    </MenuItem>

                    {!window.location.pathname.includes('keymoments/library') && (
                        <MenuItem value={DimensionCardsMulti.MEDIUM}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box width={24} display={'flex'} justifyContent={'center'}>
                              <PersonalVideoIcon sx={{ fontSize: '18px' }} />
                            </Box>
                            <Typography variant="body2">Medium</Typography>
                          </Box>
                        </MenuItem>
                    )}

                    <MenuItem value={DimensionCardsMulti.LARGE}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box width={24} display={'flex'} justifyContent={'center'}>
                          <PersonalVideoIcon sx={{ fontSize: '24px' }} />
                        </Box>
                        <Typography variant="body2">Large</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
            )}

            {['library'].includes(location.pathname.split('/')[location.pathname.split('/').length-1]) &&
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <FormControl size="small" sx={{ width: 150 }}>
                  <InputLabel>Sort by</InputLabel>

                  <Select
                    IconComponent={null}
                    value={sortField}
                    label="Sort by"
                    onChange={(e) => setSortField(e.target.value)}
                    input={
                      <OutlinedInput
                        endAdornment={
                          <InputAdornment position="end" sx={{ ml: 0 }}>
                            <IconButton
                              size="large"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleOrder();
                              }}
                              sx={{
                                p: 0,
                                width: 'auto',
                                height: 'auto',
                                minWidth: 0,
                                border: '0px solid transparent',
                                "&:hover": {
                                  backgroundColor: 'transparent',
                                },
                              }}
                            >
                              {order === "desc" ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
                            </IconButton>
                          </InputAdornment>
                        }
                        sx={{
                          pr: 1,
                          '& .MuiOutlinedInput-input': {
                            overflow: 'visible',
                            textOverflow: 'unset',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    }
                  >
                    {KEY_MOMENTS_SORTABLE_FIELDS[CURRENT_SPORT].platform.map((el) => (
                      <MenuItem key={el.id} value={el.id}>{el.value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <IconButton
                aria-label="filter"
                size="small"
                onClick={()=>setFiltersDrawerOpen(prev => !prev)}
                sx={circularIconHoverSx()}
              >
                {filtersDrawerOpen ? <FilterListOffIcon sx={{ fontSize: HEADER_ACTION_ICON_SIZE }} /> : <FilterListIcon sx={{ fontSize: HEADER_ACTION_ICON_SIZE }} />}
              </IconButton>
            </>}

            {runningIngestionJobs.length > 0 && [EVENTS_PAGE_NAME].includes(location.pathname.split('/').at(-1)) &&
            <IconButton
              aria-label="pin carousel"
              size="small"
              data-tour={"pin_carousel_button"}
              disabled={ingestionViewMode === 'grid' || !hasLiveJobs}
              onClick={() => setCarouselPinned(v => !v)}
              onMouseEnter={() => setPinHovered(true)}
              onMouseLeave={() => setPinHovered(false)}
              sx={circularIconHoverSx()}
            >
              {carouselPinned
                ? pinHovered
                  ? <SlashedIcon active={true}><PushPinIcon sx={{ fontSize: HEADER_ACTION_ICON_SIZE }} /></SlashedIcon>
                  : <PushPinIcon sx={{ fontSize: HEADER_ACTION_ICON_SIZE }} />
                : pinHovered
                  ? <PushPinIcon sx={{ fontSize: HEADER_ACTION_ICON_SIZE }} />
                  : <SlashedIcon active={false}><PushPinIcon sx={{ fontSize: HEADER_ACTION_ICON_SIZE }} /></SlashedIcon>
              }
            </IconButton>}

            {runningIngestionJobs.length > 0 && [EVENTS_PAGE_NAME].includes(location.pathname.split('/').at(-1)) &&
            <IconButton
              aria-label="toggle ingestion view"
              size="small"
              disabled={ingestionViewMode === 'carousel' && !carouselOverflows}
              onClick={() => setIngestionViewMode(v => v === 'carousel' ? 'grid' : 'carousel')}
              sx={circularIconHoverSx()}
            >
              {ingestionViewMode === 'carousel'
                ? <GridViewIcon sx={{ fontSize: HEADER_ACTION_ICON_SIZE }} />
                : <ViewCarouselIcon sx={{ fontSize: HEADER_ACTION_ICON_SIZE }} />}
            </IconButton>}

            {(([EVENTS_PAGE_NAME].includes(location.pathname.split('/')[location.pathname.split('/').length-1]) && runningIngestionJobs.length > 0) || ['keymoments','clips','library', BACKUP_PAGE_NAME].includes(location.pathname.split('/')[location.pathname.split('/').length-1])) &&
                <VolumeControl
                    volume={volume}
                    muted={muted}
                    onVolumeChange={handleVolumeChange}
                    onToggleMute={handleToggleMute}
                    iconSize={HEADER_ACTION_ICON_SIZE}
                />
            }

            {shouldShowDeleteButton && (
                <Tooltip title={getTooltipTitle()}>
                  <IconButton
                      data-tour={"trash_icon_breadcrumbs"}
                      ref={anchorRef}
                      onClick={() => setOpenPopper(true)}
                      disabled={isDeleteDisabled}
                      sx={circularIconHoverSx()}
                  >
                    <DeleteIcon
                        sx={{ fontSize: 20 }}
                        color={isDeleteDisabled ? "disabled" : "action"}
                    />
                  </IconButton>
                </Tooltip>
            )}

            <StyledPopper
                message={`Do you want to delete the selected ${isKeyMoments ? 'key moments' : 'clips'}`}
                anchorEl={anchorRef.current}
                afterResponse={(option)=>{deleteSelectedCards(option)}}
                open={openPopper}
            />

            {['keymoments','clips', BACKUP_PAGE_NAME].includes(location.pathname.split('/')[location.pathname.split('/').length-1]) && <IconButton
              aria-label="filter"
              size="small"
              onClick={() => { handleToggle()}}
              sx={circularIconHoverSx()}
              data-tour={"filters_drawer"}
            >
              <Tooltip title={"Add Filters"}>
                <FilterListIcon />
              </Tooltip>
            </IconButton>
            }

            {['library'].includes(location.pathname.split('/')[location.pathname.split('/').length-1]) &&
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  if (historyPreviousPage.includes('editcollection')) navigate(-2);
                  else navigate(-1);                  
                }}
              >
                Close Library
              </Button>}

          </Box>
        </Box>}
        
        {/* {title && titleBottom && (
          <Grid sx={{ mt: card === false ? 0.25 : 1 }}>
            <Typography variant="h2">
              <FormattedMessage id={custom ? heading : item?.title} />
            </Typography>
          </Grid>
        )} */}
      </Grid>
      {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
    </MainCard>
  );
}

Breadcrumbs.propTypes = {
  card: PropTypes.bool,
  custom: PropTypes.bool,
  divider: PropTypes.bool,
  heading: PropTypes.string,
  icon: PropTypes.bool,
  icons: PropTypes.bool,
  links: PropTypes.array,
  maxItems: PropTypes.number,
  rightAlign: PropTypes.bool,
  separator: PropTypes.any,
  title: PropTypes.bool,
  titleBottom: PropTypes.bool,
  sx: PropTypes.any,
  others: PropTypes.any
};
